import apiClient from './api';
import type { PackageType } from './software-update-package.service';
import type {
  Rollout,
  RolloutEvent,
  RolloutEventsResult,
  RolloutEventsTimeseries,
} from './software-update-rollout.service';

// ─── Endpoint 1: GET /admin/software-update/overview ─────────────────────────

export interface OverviewTotals {
  /** Count of rollouts with status=active (not window-dependent). */
  active_rollouts: number;
  /** count(DISTINCT device_id) over terminal events in the window. */
  active_devices: number;
  download_success: number;
  download_failed: number;
  download_rejected: number;
  install_success: number;
  install_failed: number;
  /**
   * (download_failed + download_rejected + install_failed) /
   * (download_success + download_failed + download_rejected + install_success + install_failed).
   * Denominator 0 → 0.
   */
  error_rate: number;
}

export interface OverviewRolloutStats {
  download_success: number;
  download_failed: number;
  download_rejected: number;
  install_success: number;
  install_failed: number;
  error_rate: number;
  /** null when the rollout has no events in the window. */
  last_event_at: string | null;
  /** Exactly 12 ints, oldest bucket first: total terminal events per bucket. */
  sparkline: number[];
}

export interface OverviewRollout {
  /** Same object shape as one item of GET /admin/software-update/rollouts. */
  rollout: Rollout;
  stats: OverviewRolloutStats;
}

export interface OverviewPackage {
  id: string;
  /** Definition-facing label, for example "Aviron Launcher" or "OS". */
  display_name: string;
  /** Technical package identifier, for example "com.avironactive.rower". */
  package_name: string;
  version: string;
  type: PackageType;
  /** Terminal events for this package in the window, across rollouts. */
  event_count: number;
  /** download_failed + download_rejected + install_failed in the window. */
  failed_count: number;
  /** install_success in the window (added 2026-08-21 for the chips redesign). */
  install_success_count: number;
  /** Number of active rollouts containing this package. */
  rollout_count: number;
}

export interface OverviewFailureReason {
  /** Empty/null reason is normalized server-side to "unknown". */
  reason: string;
  count: number;
}

export interface OverviewProblemDevice {
  device_id: string;
  machine_id: string;
  serial_number: string;
  is_internal: boolean;
  failure_count: number;
  last_reason: string;
  last_event_at: string;
}

export interface SoftwareUpdateOverview {
  totals: OverviewTotals;
  /** One entry per ACTIVE rollout, including ones with zero events. */
  rollouts: OverviewRollout[];
  /** Every package attached to any active rollout, even with 0 events. */
  packages: OverviewPackage[];
  /** Top 10 reasons by count desc. */
  failure_reasons: OverviewFailureReason[];
  /** Requested 10-device page, ordered by failure_count desc. */
  problem_devices: OverviewProblemDevice[];
  /** Total problem devices before applying problem_device_offset. */
  problem_devices_total: number;
}

// ─── Endpoint 3: GET /admin/software-update/overview/events ──────────────────
// Same shape as RolloutEventsResult, with rollout_name added on each item.

export interface OverviewEvent extends RolloutEvent {
  rollout_name: string;
}

export interface OverviewEventsResult extends Omit<RolloutEventsResult, 'events'> {
  events: OverviewEvent[];
}

// ─── Endpoint 4: GET /admin/software-update/overview/stuck-installs ──────────
// Current snapshot, not bound to since/until: OS items whose download succeeded
// at least stuck_after_seconds ago (server time) with no install result yet.

export interface OverviewStuckInstall {
  command_item_id: string;
  command_id: string;
  attempt_id: string;
  device_id: string;
  machine_id: string;
  serial_number: string;
  is_internal: boolean;
  /** Any rollout status: a paused or finished rollout's open item still counts. */
  rollout_id: string;
  rollout_name: string;
  package_id: string;
  package_name: string;
  target_version: string;
  /** Server time the download success was recorded. */
  downloaded_at: string;
  /** Seconds between downloaded_at and as_of, computed server-side. */
  stuck_seconds: number;
  /** Newest event for this command + package; null when none was recorded. */
  latest_event_type: string | null;
  latest_event_at: string | null;
}

export interface OverviewStuckInstallsResult {
  /** Requested 10-item page, oldest download first. */
  items: OverviewStuckInstall[];
  total: number;
  as_of: string;
}

// ─── Query params ────────────────────────────────────────────────────────────

export interface OverviewParams {
  since: string;
  until: string;
  problem_device_offset?: number;
  /** When false, internal devices are excluded from every aggregate. */
  include_internal: boolean;
}

export interface OverviewTimeseriesParams {
  since: string;
  until: string;
  bucket_seconds: number;
  event_type?: string;
  reason?: string;
  package_id?: string;
  rollout_id?: string;
  include_internal: boolean;
}

export interface OverviewEventsParams {
  since: string;
  until: string;
  limit?: number;
  offset?: number;
  event_type?: string[];
  reason?: string[];
  package_id?: string;
  rollout_id?: string[];
  machine_id?: string;
  include_internal: boolean;
}

export interface OverviewStuckInstallsParams {
  /** Required; must be positive. */
  stuck_after_seconds: number;
  offset?: number;
  include_internal: boolean;
}

const base = '/admin/software-update/overview';

export const softwareUpdateOverviewService = {
  getOverview: async (params: OverviewParams): Promise<SoftwareUpdateOverview> => {
    const search = new URLSearchParams();
    search.set('since', params.since);
    search.set('until', params.until);
    if (params.problem_device_offset) {
      search.set('problem_device_offset', String(params.problem_device_offset));
    }
    search.set('include_internal', String(params.include_internal));
    return apiClient.get<SoftwareUpdateOverview>(`${base}?${search.toString()}`);
  },
  getEventsTimeseries: async (
    params: OverviewTimeseriesParams,
  ): Promise<RolloutEventsTimeseries> => {
    const search = new URLSearchParams();
    search.set('since', params.since);
    search.set('until', params.until);
    search.set('bucket_seconds', String(params.bucket_seconds));
    if (params.event_type) search.set('event_type', params.event_type);
    if (params.reason) search.set('reason', params.reason);
    if (params.package_id) search.set('package_id', params.package_id);
    if (params.rollout_id) search.set('rollout_id', params.rollout_id);
    search.set('include_internal', String(params.include_internal));
    return apiClient.get<RolloutEventsTimeseries>(
      `${base}/events/timeseries?${search.toString()}`,
    );
  },
  listEvents: async (params: OverviewEventsParams): Promise<OverviewEventsResult> => {
    const search = new URLSearchParams();
    search.set('since', params.since);
    search.set('until', params.until);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    for (const eventType of params.event_type ?? []) search.append('event_type', eventType);
    for (const reason of params.reason ?? []) search.append('reason', reason);
    if (params.package_id) search.set('package_id', params.package_id);
    for (const rolloutID of params.rollout_id ?? []) search.append('rollout_id', rolloutID);
    if (params.machine_id) search.set('machine_id', params.machine_id);
    search.set('include_internal', String(params.include_internal));
    return apiClient.get<OverviewEventsResult>(`${base}/events?${search.toString()}`);
  },
  listStuckInstalls: async (
    params: OverviewStuckInstallsParams,
  ): Promise<OverviewStuckInstallsResult> => {
    const search = new URLSearchParams();
    search.set('stuck_after_seconds', String(params.stuck_after_seconds));
    if (params.offset) search.set('offset', String(params.offset));
    search.set('include_internal', String(params.include_internal));
    return apiClient.get<OverviewStuckInstallsResult>(
      `${base}/stuck-installs?${search.toString()}`,
    );
  },
};

export default softwareUpdateOverviewService;
